import React from 'react'
import { AndroidFilled, AppleFilled, ChromeFilled } from '@ant-design/icons'
import moment from 'moment-timezone'
import type { FilterDefinition } from '@pushly/aqe/lib/components'
import { FILTER_OPERATORS } from '@pushly/aqe/lib/components'
import { DeliveryChannel } from '@pushly/aqe/lib/enums/delivery-channels'
import { getPathEntityId } from '@pushly/aqe/lib/utils/get-path-entity-id'
import { Container } from 'typescript-ioc/es5'
import { ReportConfigurationType } from '../enums/report-configuration-type'
import { ReportConfigurationLevel } from '../enums/report-configuration-level'
import { ReportConfigurationBreakdown } from '../enums/report-configuration-breakdown'
import { PlatformLevel } from '../enums/platform-level'
import { NOTIFICATION_SOURCE_OPTIONS } from './notification-source-label'
import { FILTER_DROPDOWN_OPTIONS } from '../constants/filter-dropdown-options'
import { EntityConditionOptions, FiltersDropdownOption } from '../types'
import { DomainService, UserService, NotificationService, JourneyService } from '../../../services'
import { PromptService } from '../../../services/prompt'
import { AppMessageService } from '../../../services/app-message.service'
import { AppState } from '../../../stores/app'
import { BASE_TIME_FORMAT } from '../../../constants'
import { getAvailableSegmentTypes } from '../../../_utils/domain'
import { getEnabledDeliveryChannels } from '../../../_utils/domain'
import { SegmentType } from '@pushly/models/lib/enums/segment-type'
import type { SegmentDto } from '../../../dtos/segment'
import type { UserDto } from '../../../dtos/user'
import type { DomainKeyword } from '../../../dtos/domain'

/**
 * Renders a rich label for entity-style filter options.
 * Title is prominent on the first line; metadata (ID + optional subtitle) is muted below.
 */
function entityLabel(
    title: string,
    id: number | string,
    subtitle?: string,
): { label: React.ReactNode; searchText: string } {
    const meta = subtitle ? `#${id} · ${subtitle}` : `#${id}`
    return {
        label: (
            <span style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.3 }}>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{title}</span>
                <span style={{ fontSize: 11, color: 'var(--color-gray-dark, #626262)', whiteSpace: 'nowrap' }}>
                    {meta}
                </span>
            </span>
        ),
        searchText: `${title} #${id}`,
    }
}

/**
 * Context passed to each filter definition factory so it can set up
 * condition closures and context-dependent behavior.
 */
interface FilterContext {
    type: ReportConfigurationType
    level: ReportConfigurationLevel
    breakdown: ReportConfigurationBreakdown
    platformLevel: PlatformLevel
    /** The current domain (DomainDto) or organization (GetAccountResponse) depending on platformLevel */
    entity: any
}

/**
 * Wraps a FiltersDropdownOption condition function (which expects EntityConditionOptions)
 * into a FilterDefinition condition function (which expects no args and returns boolean).
 */
function wrapCondition(
    optCondition: ((opts: EntityConditionOptions) => boolean) | undefined,
    ctx: FilterContext,
): (() => boolean) | undefined {
    if (!optCondition) return undefined

    return () => {
        const conditionOpts = {
            type: ctx.type,
            level: ctx.level,
            breakdown: ctx.breakdown as string,
            platformLevel: ctx.platformLevel,
            ...(ctx.platformLevel === PlatformLevel.DOMAIN ? { domain: ctx.entity } : { org: ctx.entity }),
        } as EntityConditionOptions

        return optCondition(conditionOpts)
    }
}

// ---------------------------------------------------------------------------
// Master Filter Registry
// Maps each filter field name to a factory that creates its FilterDefinition.
// ---------------------------------------------------------------------------

type FilterFactory = (ctx: FilterContext) => Omit<FilterDefinition, 'key'>

const FILTER_DEFINITION_REGISTRY: Record<string, FilterFactory> = {
    // -----------------------------------------------------------------------
    // Static multi-select filters
    // -----------------------------------------------------------------------
    BROWSER: () => ({
        label: 'Browser',
        type: 'multi-select',
        operators: [
            { value: 'in', label: 'is' },
            { value: 'not_in', label: 'is not' },
        ],
        selectOptions: [
            // Common browsers
            { value: 'Chrome', label: 'Chrome', group: 'common', groupLabel: 'Common' },
            { value: 'Edge', label: 'Edge', group: 'common', groupLabel: 'Common' },
            { value: 'Firefox', label: 'Firefox', group: 'common', groupLabel: 'Common' },
            { value: 'Safari', label: 'Safari', group: 'common', groupLabel: 'Common' },
            // Other browsers
            { value: 'Internet Explorer', label: 'Internet Explorer', group: 'other', groupLabel: 'Other' },
            { value: 'Opera', label: 'Opera', group: 'other', groupLabel: 'Other' },
            { value: 'Vivaldi', label: 'Vivaldi', group: 'other', groupLabel: 'Other' },
            { value: 'Webview', label: 'Webview', group: 'other', groupLabel: 'Other' },
            { value: 'Yandex Browser', label: 'Yandex Browser', group: 'other', groupLabel: 'Other' },
            { value: 'UNKNOWN', label: 'Unknown', group: 'other', groupLabel: 'Other' },
        ],
    }),

    OPERATING_SYSTEM: () => ({
        label: 'Operating System',
        type: 'multi-select',
        operators: [
            { value: 'in', label: 'is' },
            { value: 'not_in', label: 'is not' },
        ],
        selectOptions: [
            // Common operating systems
            { value: 'Android', label: 'Android', group: 'common', groupLabel: 'Common' },
            { value: 'Mac', label: 'Mac OS', group: 'common', groupLabel: 'Common' },
            { value: 'Windows', label: 'Windows', group: 'common', groupLabel: 'Common' },
            { value: 'iOS', label: 'iOS', group: 'common', groupLabel: 'Common' },
            // Other operating systems
            { value: 'BSD', label: 'BSD', group: 'other', groupLabel: 'Other' },
            { value: 'ChromeOS', label: 'Chrome OS', group: 'other', groupLabel: 'Other' },
            { value: 'Linux', label: 'Linux', group: 'other', groupLabel: 'Other' },
        ],
    }),

    PLACEMENT: () => ({
        label: 'Device Type',
        type: 'multi-select',
        operators: [
            { value: 'in', label: 'is' },
            { value: 'not_in', label: 'is not' },
        ],
        selectOptions: [
            { value: 'desktop', label: 'Desktop' },
            { value: 'mobile', label: 'Mobile' },
            { value: 'tablet', label: 'Tablet' },
        ],
    }),

    CHANNEL: () => ({
        label: 'Delivery Channel',
        type: 'multi-select',
        operators: [
            { value: 'in', label: 'is' },
            { value: 'not_in', label: 'is not' },
        ],
        selectOptions: [
            { value: DeliveryChannel.WEB, label: 'Web', icon: <ChromeFilled /> },
            { value: DeliveryChannel.NATIVE_IOS, label: 'Native Apple', icon: <AppleFilled /> },
            { value: DeliveryChannel.NATIVE_ANDROID, label: 'Native Android', icon: <AndroidFilled /> },
        ],
    }),

    NOTIFICATION_SOURCE: () => ({
        label: 'Source',
        type: 'multi-select',
        operators: [
            { value: 'in', label: 'is' },
            { value: 'not_in', label: 'is not' },
        ],
        selectOptions: [...NOTIFICATION_SOURCE_OPTIONS],
    }),

    APP_MESSAGE_STYLE: () => ({
        label: 'Style',
        type: 'multi-select',
        operators: [
            { value: 'in', label: 'is' },
            { value: 'not_in', label: 'is not' },
        ],
        selectOptions: [
            { value: 'BANNER', label: 'Banner' },
            { value: 'FULLSCREEN', label: 'Fullscreen' },
            { value: 'MODAL', label: 'Modal' },
        ],
    }),

    'NOTIFICATION.REPLACEMENT_BEHAVIOR': () => ({
        label: 'Replacement Behavior',
        type: 'multi-select',
        operators: [
            { value: 'in', label: 'is' },
            { value: 'not_in', label: 'is not' },
        ],
        selectOptions: [
            { value: 'Stack', label: 'Stack' },
            { value: 'Replace All', label: 'Replace All' },
            { value: 'Replacement Group', label: 'Replacement Group' },
        ],
    }),

    'NOTIFICATION.CONTENT_SOURCE': () => ({
        label: 'Content Source',
        type: 'multi-select',
        operators: [
            { value: 'in', label: 'is' },
            { value: 'not_in', label: 'is not' },
        ],
        selectOptions: [
            { value: 'manual', label: 'Manual' },
            { value: 'pushly_ai', label: 'Pushly AI' },
            { value: 'page_analyzer', label: 'Page Analyzer' },
        ],
    }),

    // -----------------------------------------------------------------------
    // Text filters
    // -----------------------------------------------------------------------
    NOTIFICATION_TITLE: () => ({
        label: 'Title',
        type: 'text',
        operators: [
            { ...FILTER_OPERATORS.contains, tagLabel: 'contains ' },
            { ...FILTER_OPERATORS.not_contains, tagLabel: 'does not contain ' },
        ],
    }),

    NOTIFICATION_BODY: () => ({
        label: 'Body',
        type: 'text',
        operators: [
            { ...FILTER_OPERATORS.contains, tagLabel: 'contains ' },
            { ...FILTER_OPERATORS.not_contains, tagLabel: 'does not contain ' },
        ],
    }),

    NOTIFICATION_LANDING_URL: () => ({
        label: 'Landing Url',
        type: 'text',
        operators: [
            { ...FILTER_OPERATORS.contains, tagLabel: 'contains ' },
            { ...FILTER_OPERATORS.not_contains, tagLabel: 'does not contain ' },
        ],
    }),

    ACTIVITY_ID: () => ({
        label: 'Activity ID',
        type: 'text',
    }),

    // -----------------------------------------------------------------------
    // Async multi-select filters (with loadOptions)
    // -----------------------------------------------------------------------
    NOTIFICATION_KEYWORDS: () => ({
        label: 'Keywords',
        type: 'multi-select',
        operators: [
            { ...FILTER_OPERATORS.eq, label: 'is any of', tagLabel: 'is any of ' },
            { ...FILTER_OPERATORS.neq, label: 'is not any of', tagLabel: 'is not any of ' },
        ],
        defaultOperator: 'eq',
        loadOptions: async () => {
            const appState = Container.get(AppState)
            const domainService = Container.get(DomainService)
            const domainId = appState.currentDomain!.id

            const result = await domainService.fetchKeywordsByDomainId(domainId, {
                query: { pagination: 0 },
                showLoadingScreen: false,
            })

            const keywords: DomainKeyword[] = result.data || []
            return keywords.map((kw) => ({ value: kw.name, label: kw.name }))
        },
    }),

    NOTIFICATION_SEGMENTS: () => {
        const loader = async () => {
            const appState = Container.get(AppState)
            const domainService = Container.get(DomainService)
            const domainId = appState.currentDomain!.id

            const { data } = await domainService.fetchSegmentsByDomainId(domainId, {
                query: {
                    includeTreated: 0,
                    pagination: 0,
                    fields: 'id,name,source,computedStatus,isDefault,iconUrl,flags,segmentType',
                    source: 'standard',
                },
                showLoadingScreen: false,
            })

            const allowedSegmentTypes = getAvailableSegmentTypes(appState.currentDomain!)
            return data
                .filter((s: SegmentDto) => {
                    return allowedSegmentTypes.includes(s.type) && s.type !== SegmentType.ALL_APP_USERS
                })
                .map((s: SegmentDto) => ({ value: s.id, label: s.name }))
        }

        return {
            label: 'Segments',
            type: 'multi-select' as const,
            operators: [
                { value: 'in' as const, label: 'is', tagLabel: 'is ' },
                { value: 'not_in' as const, label: 'is not', tagLabel: 'is not ' },
                { value: 'any' as const, label: 'contains any of', tagLabel: 'contains any of ' },
                { value: 'not_any' as const, label: 'does not contain any of', tagLabel: 'does not contain any of ' },
            ],
            loadOptions: loader,
            resolveFilterValues: loader,
        }
    },

    SEGMENT_ID: () => {
        const loader = async () => {
            const appState = Container.get(AppState)
            const domainService = Container.get(DomainService)
            const domainId = appState.currentDomain!.id

            const { data } = await domainService.fetchSegmentsByDomainId(domainId, {
                query: {
                    includeTreated: 0,
                    pagination: 0,
                    fields: 'id,name,source,computedStatus,isDefault,iconUrl,flags,segmentType',
                    source: 'standard',
                },
                showLoadingScreen: false,
            })

            const allowedSegmentTypes = getAvailableSegmentTypes(appState.currentDomain!)
            return data
                .filter((s: SegmentDto) => allowedSegmentTypes.includes(s.type))
                .map((s: SegmentDto) => ({ value: s.id, label: s.name }))
        }

        return {
            label: 'Segments',
            type: 'multi-select' as const,
            operators: [
                { value: 'in' as const, label: 'is', tagLabel: 'is ' },
                { value: 'not_in' as const, label: 'is not', tagLabel: 'is not ' },
            ],
            loadOptions: loader,
            resolveFilterValues: loader,
        }
    },

    NOTIFICATION_CREATED_BY: () => {
        const loader = async () => {
            const userService = Container.get(UserService)

            const domainUsers = await userService.fetchUsersForCurrentDomain({
                query: { pagination: 0 },
            })

            if (!domainUsers) return []

            return domainUsers
                .filter((u: UserDto) => !u.isInternalUser)
                .map((u: UserDto) => ({ value: u.id, label: u.name }))
        }

        return {
            label: 'Created By',
            type: 'multi-select' as const,
            operators: [
                { value: 'in' as const, label: 'is', tagLabel: 'is ' },
                { value: 'not_in' as const, label: 'is not', tagLabel: 'is not ' },
            ],
            loadOptions: loader,
            resolveFilterValues: loader,
        }
    },

    DOMAIN_ID: (ctx) => {
        const loader = async () => {
            const appState = Container.get(AppState)
            const pathOrgId = getPathEntityId('organization')

            if (!pathOrgId) return []

            const currentUserOrgDomains =
                appState.currentUserDomains?.filter((d) => d.accountId?.toString() === pathOrgId.toString()) ?? []

            return currentUserOrgDomains.map((d) => ({
                value: d.id,
                label: d.displayName,
            }))
        }

        return {
            label: 'Domain',
            type: 'multi-select' as const,
            // The domain is the tenancy boundary of an insights request. The insights API folds a
            // domain filter into the request's scope, which only equality and membership can
            // express, so it refuses "is not".
            operators: [{ value: 'in' as const, label: 'is', tagLabel: 'is ' }],
            condition: () => ctx.platformLevel === PlatformLevel.ORG,
            loadOptions: loader,
            resolveFilterValues: loader,
        }
    },

    // -----------------------------------------------------------------------
    // Search-based multi-select filters (using searchOptions / loadOptions)
    // -----------------------------------------------------------------------
    NOTIFICATION_ID: () => ({
        label: 'Notification',
        type: 'multi-select',
        operators: [
            { value: 'in', label: 'is', tagLabel: 'is ' },
            { value: 'not_in', label: 'is not', tagLabel: 'is not ' },
        ],
        formatTagOptionLabel: (opt) => {
            const n = opt.data as { title: string; id: number } | undefined
            return n ? `#${n.id}: ${n.title}` : String(opt.value)
        },
        resolveFilterValues: async (ids: number[]) => {
            const appState = Container.get(AppState)
            const notifService = Container.get(NotificationService)
            const domainId = appState.currentDomain!.id
            const tz = appState.currentDomain!.timezone

            const { notifications } = await notifService.fetchNotificationsByDomainId(domainId, {
                search: ids.join(','),
                pagination: 0,
                source: 'MANUAL,CAMPAIGN,FEED',
                status: 'ACTIVE,COMPLETED,SCHEDULED,QUEUED,DELIVERING',
            })

            return notifications
                .filter((n: any) => ids.includes(n.id))
                .map((n: any) => {
                    const title = n.defaultTemplate?.title ?? 'Untitled'
                    const sendDate = n.deliverySpec?.sendDateUtc
                        ? `sent ${moment.tz(n.deliverySpec.sendDateUtc, tz).format(BASE_TIME_FORMAT)}`
                        : undefined
                    return {
                        value: n.id,
                        ...entityLabel(title, n.id, sendDate),
                        data: { title, id: n.id },
                    }
                })
        },
        searchOptions: async (query: string) => {
            const appState = Container.get(AppState)
            const notifService = Container.get(NotificationService)
            const domainId = appState.currentDomain!.id
            const tz = appState.currentDomain!.timezone

            const { notifications } = await notifService.fetchNotificationsByDomainId(domainId, {
                search: query,
                pagination: 0,
                source: 'MANUAL,CAMPAIGN,FEED',
                status: 'ACTIVE,COMPLETED,SCHEDULED,QUEUED,DELIVERING',
            })

            return notifications.map((n: any) => {
                const title = n.defaultTemplate?.title ?? 'Untitled'
                const sendDate = n.deliverySpec?.sendDateUtc
                    ? `sent ${moment.tz(n.deliverySpec.sendDateUtc, tz).format(BASE_TIME_FORMAT)}`
                    : undefined
                return {
                    value: n.id,
                    ...entityLabel(title, n.id, sendDate),
                    data: { title, id: n.id },
                }
            })
        },
        searchDebounce: 450,
    }),

    CAMPAIGN_ID: () => ({
        label: 'Campaign',
        type: 'multi-select',
        operators: [
            { value: 'in', label: 'is', tagLabel: 'is ' },
            { value: 'not_in', label: 'is not', tagLabel: 'is not ' },
        ],
        formatTagOptionLabel: (opt) => {
            const c = opt.data as { name: string; id: number } | undefined
            return c ? `#${c.id}: ${c.name}` : String(opt.value)
        },
        resolveFilterValues: async (ids: number[]) => {
            const journeyService = Container.get(JourneyService)

            const res = await journeyService.fetchAll({
                query: { search: ids.join(','), pagination: 0 },
                showLoadingScreen: false,
            })

            if (!res.ok) return []

            return (res.data ?? [])
                .filter((c: any) => ids.includes(c.id))
                .map((c: any) => ({
                    value: c.id,
                    ...entityLabel(c.name, c.id),
                    data: { name: c.name, id: c.id },
                }))
        },
        searchOptions: async (query: string) => {
            const journeyService = Container.get(JourneyService)

            const res = await journeyService.fetchAll({
                query: { search: query, pagination: 0 },
                showLoadingScreen: false,
            })

            if (!res.ok) return []

            return (res.data ?? []).map((c: any) => ({
                value: c.id,
                ...entityLabel(c.name, c.id),
                data: { name: c.name, id: c.id },
            }))
        },
        searchDebounce: 450,
    }),

    PROMPT_ID: () => ({
        label: 'Prompt',
        type: 'multi-select',
        operators: [
            { value: 'in', label: 'is', tagLabel: 'is ' },
            { value: 'not_in', label: 'is not', tagLabel: 'is not ' },
        ],
        formatTagOptionLabel: (opt) => {
            const p = opt.data as { name: string; id: number } | undefined
            return p ? `#${p.id}: ${p.name}` : String(opt.value)
        },
        resolveFilterValues: async (ids: number[]) => {
            const appState = Container.get(AppState)
            const promptService = Container.get(PromptService)
            const domainId = appState.currentDomain!.id

            const res = await promptService.fetchPromptGroupsByDomainId(domainId, {
                query: {
                    search: ids.join(','),
                    pagination: 0,
                    channels: getEnabledDeliveryChannels(appState.currentDomain!).join(','),
                    include_deleted: 1,
                },
            })

            if (!res.ok || !res.data) return []

            const options: { value: number; label: React.ReactNode; searchText?: string; data: any }[] = []
            for (const group of res.data) {
                for (const prompt of group.prompts) {
                    if (!ids.includes(prompt.id)) continue
                    const name = prompt.name || prompt.computedName || group.name || 'Unknown Prompt'
                    options.push({
                        value: prompt.id,
                        ...entityLabel(name, prompt.id),
                        data: { name, id: prompt.id },
                    })
                }
            }
            return options
        },
        searchOptions: async (query: string) => {
            const appState = Container.get(AppState)
            const promptService = Container.get(PromptService)
            const domainId = appState.currentDomain!.id

            const res = await promptService.fetchPromptGroupsByDomainId(domainId, {
                query: {
                    search: query,
                    pagination: 0,
                    channels: getEnabledDeliveryChannels(appState.currentDomain!).join(','),
                    include_deleted: 1,
                },
            })

            if (!res.ok || !res.data) return []

            const options: { value: number; label: React.ReactNode; searchText?: string; data: any }[] = []
            for (const group of res.data) {
                for (const prompt of group.prompts) {
                    const name = prompt.name || prompt.computedName || group.name || 'Unknown Prompt'
                    options.push({
                        value: prompt.id,
                        ...entityLabel(name, prompt.id),
                        data: { name, id: prompt.id },
                    })
                }
            }
            return options
        },
        searchDebounce: 450,
    }),

    TEST_ID: (ctx) => {
        const loader = async () => {
            const appState = Container.get(AppState)
            const domainId = appState.currentDomain!.id

            const isSubscriberPromptView =
                ctx.type === ReportConfigurationType.SUBSCRIBER && ctx.level === ReportConfigurationLevel.PROMPT

            if (isSubscriberPromptView) {
                const promptService = Container.get(PromptService)
                const res = await promptService.fetchPromptGroupsByDomainId(domainId, {
                    query: {
                        pagination: 0,
                        channels: getEnabledDeliveryChannels(appState.currentDomain!).join(','),
                        is_ab_test: true,
                        include_deleted: 1,
                    },
                })

                if (!res.ok || !res.data) return []

                return res.data.map((group) => ({
                    value: group.id,
                    ...entityLabel(group.name, group.id),
                    data: { name: group.name, id: group.id },
                }))
            }

            const notifService = Container.get(NotificationService)
            const { ok, data: tests } = await notifService.fetchTestsByDomainId(domainId, {
                pagination: 0,
                fields: 'id,name',
            })

            if (!ok) return []

            return tests
                .sort((a: any, b: any) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))
                .map((t: any) => ({
                    value: t.id,
                    ...entityLabel(t.name, t.id),
                    data: { name: t.name, id: t.id },
                }))
        }

        return {
            label: 'A/B Test',
            type: 'multi-select' as const,
            operators: [
                { value: 'in' as const, label: 'is', tagLabel: 'is ' },
                { value: 'not_in' as const, label: 'is not', tagLabel: 'is not ' },
                { value: 'is_not_null' as const, label: 'exists', tagLabel: 'exists' },
                { value: 'is_null' as const, label: 'does not exist', tagLabel: 'does not exist' },
            ],
            formatTagOptionLabel: (opt) => {
                const t = opt.data as { name: string; id: number } | undefined
                return t ? `#${t.id}: ${t.name}` : String(opt.value)
            },
            loadOptions: loader,
            resolveFilterValues: loader,
        }
    },

    APP_MESSAGE_ID: () => ({
        label: 'App Message',
        type: 'multi-select',
        operators: [
            { value: 'in', label: 'is', tagLabel: 'is ' },
            { value: 'not_in', label: 'is not', tagLabel: 'is not ' },
        ],
        formatTagOptionLabel: (opt) => {
            const m = opt.data as { name: string; id: number } | undefined
            return m ? `#${m.id}: ${m.name}` : String(opt.value)
        },
        resolveFilterValues: async (ids: number[]) => {
            const appState = Container.get(AppState)
            const appMsgService = Container.get(AppMessageService)
            const domainId = appState.currentDomain!.id

            const { messages, cancelled } = await appMsgService.fetchAppMessagesByDomainId(domainId, {
                search: ids.join(','),
                pagination: 0,
                showLoadingScreen: false,
            })

            if (cancelled) return []

            return messages
                .filter((m: any) => ids.includes(m.id))
                .map((m: any) => ({
                    value: m.id,
                    ...entityLabel(m.name, m.id),
                    data: { name: m.name, id: m.id },
                }))
        },
        searchOptions: async (query: string) => {
            const appState = Container.get(AppState)
            const appMsgService = Container.get(AppMessageService)
            const domainId = appState.currentDomain!.id

            const { messages, cancelled } = await appMsgService.fetchAppMessagesByDomainId(domainId, {
                search: query,
                pagination: 0,
                showLoadingScreen: false,
            })

            if (cancelled) return []

            return messages.map((m: any) => ({
                value: m.id,
                ...entityLabel(m.name, m.id),
                data: { name: m.name, id: m.id },
            }))
        },
        searchDebounce: 450,
    }),
}

// ---------------------------------------------------------------------------
// Main builder function
// ---------------------------------------------------------------------------

/**
 * Builds a FilterDefinition[] for the current report context by looking up
 * available filters from FILTER_DROPDOWN_OPTIONS and mapping each to a full
 * FilterDefinition via the master registry.
 *
 * @param type - Report configuration type (DELIVERY, SUBSCRIBER, etc.)
 * @param level - Report configuration level (DOMAIN, NOTIFICATION, etc.)
 * @param breakdown - Report configuration breakdown
 * @param platformLevel - Whether viewing at domain or org level
 * @param entity - The current domain or organization entity
 * @returns FilterDefinition[] sorted alphabetically by label
 */
export function buildReportingFilterDefinitions(
    type: ReportConfigurationType,
    level: ReportConfigurationLevel,
    breakdown: ReportConfigurationBreakdown,
    platformLevel: PlatformLevel,
    entity: any,
): FilterDefinition[] {
    // 1. Build the lookup key
    const filterKey = `${type}_${level}_${breakdown}` as keyof typeof FILTER_DROPDOWN_OPTIONS
    const availableFilters: FiltersDropdownOption[] = FILTER_DROPDOWN_OPTIONS[filterKey] ?? []

    // 2. Build the context for factory functions and condition wrappers
    const ctx: FilterContext = { type, level, breakdown, platformLevel, entity }

    // 3. Map each available filter option to a FilterDefinition
    const definitions: FilterDefinition[] = []

    for (const filterOption of availableFilters) {
        const factory = FILTER_DEFINITION_REGISTRY[filterOption.value]
        if (!factory) {
            if (process.env.NODE_ENV === 'development') {
                console.warn(`[reporting-filter-definitions] No registry entry for filter "${filterOption.value}"`)
            }
            continue
        }

        const baseDef = factory(ctx)
        const dropdownCondition = wrapCondition(filterOption.condition, ctx)

        // Merge conditions: both the registry condition (e.g. platform level check)
        // and the dropdown condition (type/level/breakdown visibility) must pass
        let mergedCondition: (() => boolean) | undefined
        if (baseDef.condition && dropdownCondition) {
            const registryCondition = baseDef.condition
            mergedCondition = () => registryCondition() && dropdownCondition()
        } else {
            mergedCondition = dropdownCondition || baseDef.condition
        }

        const definition: FilterDefinition = {
            ...baseDef,
            key: filterOption.value,
            // Use the dropdown option label as override (it may differ from registry default)
            label: filterOption.label || baseDef.label,
            ...(mergedCondition ? { condition: mergedCondition } : {}),
        }

        definitions.push(definition)
    }

    // 4. Sort alphabetically by label (matching existing determineFilterOptions behavior)
    definitions.sort((a, b) => a.label.localeCompare(b.label))

    return definitions
}
